/**
 * "Is this still the answer to the last question I asked?"
 *
 * A screen that refetches on a change — a search box, a sign-in, a page
 * number — can have two requests in flight at once, and nothing makes them
 * come back in the order they left. Without a guard the slower, older one
 * lands last and repaints the screen with data for a question nobody is
 * asking any more: the admin list showing the results for "al" under a search
 * box that says "alice", or the You tab showing an account's synced stats a
 * moment after signing out of it.
 *
 * The guard is a counter. `begin()` takes a ticket; `isCurrent(ticket)` is
 * true only for the newest one. A response whose ticket is stale is dropped
 * on the floor — it is not an error, and it must not touch the store, the
 * busy flag or the error line, all of which now belong to the newer request.
 *
 * Deliberately not an `AbortController`: the I/O modules here return typed
 * outcomes rather than throwing, and an aborted fetch would have to be
 * threaded through every one of them as a new outcome. Dropping the answer
 * is the whole requirement.
 */
export class RequestSequence {
  private latest = 0;

  /** Start a request. Every earlier ticket is stale from this moment. */
  begin(): number {
    this.latest += 1;
    return this.latest;
  }

  /** Whether `ticket` is still the newest request. */
  isCurrent(ticket: number): boolean {
    return ticket === this.latest;
  }

  /** Make every outstanding ticket stale without starting a request — leaving the screen, signing out. */
  invalidate(): void {
    this.latest += 1;
  }
}
